"use client";
import { useEffect, useState } from "react";
import { useAuth } from "@clerk/nextjs";
import { usePathname } from "next/navigation";

/**
 * Parse guest user ID from the casey_guest_token cookie
 * Returns null if no guest token exists or if the token is invalid
 */
function parseGuestIdFromCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    const parts = cookie.trim().split("=");
    const name = parts[0];
    const value = parts[1];

    if (name === "casey_guest_token" && value) {
      try {
        const token = decodeURIComponent(value);
        const tokenParts = token.split(".");
        if (tokenParts.length > 1 && tokenParts[1]) {
          const payload = JSON.parse(atob(tokenParts[1]));
          return payload.sub || null;
        }
      } catch (error) {
        console.error("Failed to parse guest token:", error);
        return null;
      }
    }
  }

  return null;
}

/**
 * Hook to get the guest user ID from the casey_guest_token cookie
 * Returns null if no guest token exists, if the token is invalid,
 * OR if the user is signed in with Clerk (Clerk auth takes priority)
 */
export function useGuestUserId(): string | null {
  const { isSignedIn, isLoaded } = useAuth();
  const pathname = usePathname();

  // Initialize with the cookie value synchronously to avoid flash of unauthorized state
  const [guestUserId, setGuestUserId] = useState<string | null>(() => {
    // On initial render, read the cookie synchronously
    // This prevents the race condition where AuthGuard checks before useEffect runs
    return parseGuestIdFromCookie();
  });

  useEffect(() => {
    // Wait for Clerk to load before making decisions
    if (!isLoaded) {
      return;
    }

    // If user is signed in with Clerk, don't use guest ID
    if (isSignedIn) {
      setGuestUserId(null);
      return;
    }

    // Not signed in with Clerk, re-check for guest token
    // (in case it was set after initial render, e.g., after creating a guest user)
    const foundGuestId = parseGuestIdFromCookie();
    setGuestUserId(foundGuestId);
  }, [isLoaded, isSignedIn, pathname]); // Re-check on navigation

  // If Clerk is loaded and user is signed in, always return null
  // (even if we have a guest cookie, Clerk auth takes priority)
  if (isLoaded && isSignedIn) {
    return null;
  }

  return guestUserId;
}
